import React from 'react';
import Yup from '~/util/yup';
import {
	editPatientPhysicianNotificationTriggersOptions,
	getAgencySettingsOptions,
	getPatientPhysiciansOptions,
	ObservationConceptTypeOptions,
	PatientPhysicianAssociationEnum
} from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { FormModal } from '~/shared/forms/FormModal';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { Button } from '~/shared/components/buttons/Button';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';

export interface PhysicianNotificationTriggerCommandModel {
	observationConceptTypeId: number;
	greaterThan?: number;
	lessThan?: number;
	parameterTimeSpan?: string;
}

const validationSchema = Yup.object().shape({
	triggers: Yup.array()
		.of(
			Yup.object().shape(
				{
					observationConceptTypeId: Yup.string().nullable().label('Observation Concept Type').required(),
					greaterThan: Yup.number()
						.nullable()
						.label('Greater Than')
						.when('lessThan', ([lessThan], schema) => {
							return lessThan
								? schema.min(lessThan, 'Greater Than must be less than Less Than')
								: schema.required('Greater or Less Than is required');
						}),
					lessThan: Yup.number()
						.nullable()
						.label('Less Than')
						.when('greaterThan', ([greaterThan], schema) => {
							return greaterThan ? schema.max(greaterThan, 'Less Than must be greater than Greater Than') : schema.required();
						}),
					parameterTimeSpan: Yup.string().nullable().label('Parameter Time Span')
				},
				[['greaterThan', 'lessThan']]
			)
		)
		.test('unique-observationConceptTypeId', 'Observation Concept Type must be unique', function (triggers) {
			if (!triggers) return true;
			const observationConceptTypeIds = triggers.map(trigger => trigger.observationConceptTypeId);
			const uniqueObservationConceptTypeIds = new Set(observationConceptTypeIds);
			return uniqueObservationConceptTypeIds.size === observationConceptTypeIds.length;
		})
});

export function PatientPhysicianTriggersFormModal({
	stayId,
	patientPhysicianId,
	closeModal
}: {
	stayId: string;
	patientPhysicianId: string;
	closeModal: () => void;
}) {
	const { data: agencySettings } = useSuspenseQuery(getAgencySettingsOptions());
	const { data: patientPhysicians } = useSuspenseQuery(getPatientPhysiciansOptions(stayId));

	const selectedPhysician = patientPhysicians.physicians.find(p => p.id === patientPhysicianId);
	if (!selectedPhysician) {
		throw new Error('Physician not found');
	}

	const defaultTriggers = agencySettings.physicianNotificationTriggerDefaults.sort(
		(a, b) => a.observationConceptTypeId - b.observationConceptTypeId
	);

	return (
		<FormModal
			title="Edit Notification Triggers"
			initialValues={{
				patientPhysicianId,
				triggers:
					selectedPhysician.patientPhysicianAssociationId === PatientPhysicianAssociationEnum.Primary &&
					selectedPhysician.physicianNotificationTriggers.length === 0
						? defaultTriggers
						: selectedPhysician.physicianNotificationTriggers
			}}
			saveMutation={{
				...editPatientPhysicianNotificationTriggersOptions,
				invalidateQueryKeys: [getPatientPhysiciansOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const triggers = formHelpers.values.triggers as PhysicianNotificationTriggerCommandModel[];
				const conceptCategoryTypeIds = triggers.map(x => x.observationConceptTypeId).filter(x => x !== null) as number[];

				const handleRemove = (index: number) => {
					formHelpers.setFieldValue(
						'triggers',
						triggers.filter((_, i) => i !== index)
					);
				};
				const handleAdd = () => {
					formHelpers.setFieldValue('triggers', [
						...triggers,
						{ observationConceptTypeId: null, greaterThan: null, lessThan: null, parameterTimeSpan: null }
					]);
				};

				return (
					<div className="grid grid-cols-1 gap-4">
						{triggers.map((x, index) => {
							const conceptCategoryTypeId = formHelpers.values.triggers[index].observationConceptTypeId
								? Number(formHelpers.values.triggers[index].observationConceptTypeId)
								: null;
							return (
								<div key={index} className="grid grid-cols-2 gap-2 rounded border p-2">
									<SelectFormField
										name={`triggers[${index}].observationConceptTypeId`}
										options={ObservationConceptTypeOptions.filter(
											x => !conceptCategoryTypeIds.includes(x.id) || x.id === conceptCategoryTypeId
										)}
									/>
									<TextFormField name={`triggers[${index}].parameterTimeSpan`} label="Parameter Time Span" />
									<TextFormField name={`triggers[${index}].greaterThan`} label="Greater Than" />
									<TextFormField name={`triggers[${index}].lessThan`} label="Less Than" />
									<div className="col-span-2 flex justify-end">
										<Button
											actionType="delete"
											appearance="outline"
											intent="destructive"
											onPress={() => handleRemove(index)}
											className="w-fit"
											label="Remove"
										/>
									</div>
								</div>
							);
						})}
						<div className="flex justify-end">
							<Button actionType="add" appearance="outline" onPress={handleAdd} className="w-fit" label="Add a new location" />
						</div>
					</div>
				);
			}}
		</FormModal>
	);
}
